import { AlertCircle, MoreVerticalIcon, UserMinusIcon } from "lucide-react";
import { useState } from "react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { PublicEvent } from "@classview/shared-types";
import { useQueryClient } from "@tanstack/react-query";

import { getEventQueryKey } from "../api/get-event";
import { useUpdateEvent } from "../api/update-event";

type RoomSystemActionsProps = {
  event: PublicEvent;
  roomSystemId: string;
};

export function RoomSystemActions({
  event,
  roomSystemId,
}: RoomSystemActionsProps) {
  const { organisation } = useOrganisation();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const {
    mutateAsync: updateEvent,
    isPending,
    error,
    isError,
  } = useUpdateEvent({
    onSuccess: (data) => {
      queryClient.setQueryData(getEventQueryKey(event.id), data);
    },
  });

  const handleRemoveRoomSystem = async () => {
    await updateEvent({
      eventId: event.id,
      orgId: organisation.id,
      event: {
        session: {
          roomSystemIds: event.session.roomSystems
            .filter((roomSystem) => roomSystem.id !== roomSystemId)
            .map((roomSystem) => roomSystem.id),
        },
      },
    });

    setIsDialogOpen(false);
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <MoreVerticalIcon className="size-4" aria-hidden={true} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DialogTrigger asChild>
            <DropdownMenuItem className="cursor-pointer text-destructive">
              <UserMinusIcon className="size-4" aria-hidden={true} />
              Remove Room System
            </DropdownMenuItem>
          </DialogTrigger>
        </DropdownMenuContent>
      </DropdownMenu>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Are you sure you would like to remove this room system?
          </DialogTitle>
          <DialogDescription>This action cannot be undone.</DialogDescription>
        </DialogHeader>
        {isError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Unexpected error</AlertTitle>
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button
            variant="destructive"
            type="submit"
            onClick={handleRemoveRoomSystem}
            isLoading={isPending}
            isAnimated={isPending}
            disabled={isPending}
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
